import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from '../../services/AdminService/admin';
import { AuthService } from '../../services/AuthService/auth';
import { CountryTermRuleResponse } from '../../models/country-config.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';
import { UpsertFieldRuleDialog } from '../../dialogs/upsert-field-rule-dialog/upsert-field-rule-dialog';

@Component({
  selector: 'app-field-rules',
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, ErrorBanner, EmptyState, PageSizeSelect],
  templateUrl: './field-rules.html',
  styleUrl: './field-rules.scss',
})
export class FieldRules implements OnInit {
  countryCodeFilter = '';
  rules = signal<CountryTermRuleResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  deletingKey = signal<string | null>(null);

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);

  constructor(private admin: AdminService, private auth: AuthService, private dialog: MatDialog) {}

  get canEdit(): boolean {
    return this.auth.getSession()?.role === 'SUPERADMIN';
  }

  get totalCount(): number {
    return this.rules().length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize()));
  }

  get pagedRules(): CountryTermRuleResponse[] {
    const start = this.page() * this.pageSize();
    return this.rules().slice(start, start + this.pageSize());
  }

  get rangeStart(): number {
    return this.totalCount === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount);
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page()) {
      return;
    }
    this.page.set(page);
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.page.set(0);

    this.admin.listTermRules(this.countryCodeFilter.trim() || undefined).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.rules.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load term rules.'));
      },
    });
  }

  openUpsertDialog(): void {
    this.dialog
      .open(UpsertFieldRuleDialog, { width: '480px' })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.load();
        }
      });
  }

  ruleKey(rule: CountryTermRuleResponse): string {
    return `${rule.countryCode}/${rule.term}/${rule.eventType}`;
  }

  deleteRule(rule: CountryTermRuleResponse): void {
    const key = this.ruleKey(rule);
    this.deletingKey.set(key);

    this.admin.deleteTermRule(rule.countryCode, rule.term, rule.eventType).subscribe({
      next: () => {
        this.deletingKey.set(null);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.deletingKey.set(null);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to remove term rule.'));
      },
    });
  }
}
